import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { noticeAPI, rolesAPI, departmentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { BarChart3, CheckCircle2, Clock, RefreshCw, Users, AlertCircle, Bell } from 'lucide-react';

/**
 * Notice Dashboard — Hospital/IT Admin + HR view of every notice + read-acknowledgement tracking
 * Item 12 from the pre-pharmacy batch.
 */
const NoticeDashboardPage = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: '', role_code: '', department: '', date_from: '', date_to: '', subject: '' });
  const [roleOptions, setRoleOptions] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [drillDown, setDrillDown] = useState(null); // { row, kind: 'ack'|'pending' }
  // Item 7 — clicking a notice row opens the full notice + acknowledge button
  const [noticeDetail, setNoticeDetail] = useState(null);
  const [ackBusy, setAckBusy] = useState(false);

  const openNotice = async (row) => {
    try {
      const res = await noticeAPI.get(row.id);
      setNoticeDetail({ ...res.data, _row: row });
    } catch {
      // Fallback to row data if /api/notices/{id} fails for any reason
      setNoticeDetail({ ...row, _row: row });
    }
  };

  const acknowledgeNotice = async () => {
    if (!noticeDetail?.id) return;
    setAckBusy(true);
    try {
      await noticeAPI.acknowledge(noticeDetail.id);
      toast.success('Notice acknowledged');
      // Refresh dashboard table in background
      await load();
      // Mutate the open dialog so user sees the "already acknowledged" state inline.
      setNoticeDetail(prev => prev ? {
        ...prev,
        acknowledged_by: [...(prev.acknowledged_by || []), user?.id].filter(Boolean),
        _row: prev._row ? { ...prev._row, ack_count: (prev._row.ack_count || 0) + 1, pending_count: Math.max(0, (prev._row.pending_count || 0) - 1) } : prev._row,
      } : prev);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Acknowledgement failed');
    }
    setAckBusy(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const res = await noticeAPI.dashboard(params);
      setRows(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dashboard');
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    rolesAPI.list().then(r => setRoleOptions((r.data || []).map(x => ({ value: x.code, label: x.name })))).catch(() => {});
    departmentsAPI.list().then(r => setDeptOptions((r.data || []).map(d => ({ value: d.name, label: d.name })))).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalNotices = rows.length;
  const totalAck = rows.reduce((s, r) => s + r.ack_count, 0);
  const totalPending = rows.reduce((s, r) => s + r.pending_count, 0);
  const fullyRead = rows.filter(r => r.pending_count === 0 && r.recipient_count > 0).length;

  return (
    <div className="space-y-5" data-testid="notice-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><BarChart3 className="h-6 w-6 text-emerald-600" /> Notice Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Track who has read each notice — filter by date, subject, category, role or department.</p>
        </div>
        <Button variant="outline" size="icon" onClick={load} data-testid="notice-dashboard-refresh"><RefreshCw size={16} /></Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={BarChart3} label="Notices" value={totalNotices} color="bg-emerald-500" />
        <KpiCard icon={CheckCircle2} label="Total Acks" value={totalAck} color="bg-blue-500" />
        <KpiCard icon={Clock} label="Pending Acks" value={totalPending} color="bg-amber-500" />
        <KpiCard icon={Users} label="Fully Read" value={fullyRead} sub={`of ${totalNotices}`} color="bg-purple-500" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Subject contains</Label>
              <Input value={filters.subject} onChange={e => setFilters(f => ({ ...f, subject: e.target.value }))} placeholder="search…" data-testid="dash-filter-subject" />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={filters.category || 'all'} onValueChange={v => setFilters(f => ({ ...f, category: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="dash-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="policy">Policy</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Role / Category</Label>
              <Select value={filters.role_code || 'all'} onValueChange={v => setFilters(f => ({ ...f, role_code: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="dash-filter-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {roleOptions.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Department</Label>
              <Select value={filters.department || 'all'} onValueChange={v => setFilters(f => ({ ...f, department: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="dash-filter-dept"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {deptOptions.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="dash-filter-from" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="dash-filter-to" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-5">
          {loading ? (
            <p className="text-center text-slate-400 py-8">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-center text-slate-400 py-8 italic">No notices match these filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="notice-dashboard-table">
                <thead>
                  <tr className="border-b text-xs text-slate-500 uppercase">
                    <th className="text-left py-2 px-2">Title</th>
                    <th className="text-left py-2 px-2">Published</th>
                    <th className="text-left py-2 px-2">Audience</th>
                    <th className="text-left py-2 px-2">Recipients</th>
                    <th className="text-left py-2 px-2">Acknowledged</th>
                    <th className="text-left py-2 px-2">Pending</th>
                    <th className="text-left py-2 px-2">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} className="border-b hover:bg-emerald-50/40 cursor-pointer transition-colors" data-testid={`notice-row-${r.id}`} onClick={() => openNotice(r)}>
                      <td className="py-2 px-2 font-medium text-emerald-700 hover:underline">{r.title}</td>
                      <td className="py-2 px-2 text-slate-500 text-xs">{r.publish_date}</td>
                      <td className="py-2 px-2 text-xs">
                        {(r.target_roles?.length === 0 && r.target_departments?.length === 0) ? (
                          <Badge variant="outline">Everyone</Badge>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {r.target_roles?.map(x => <Badge key={x} variant="outline" className="text-[10px] bg-blue-50 text-blue-700 capitalize">{x.replace('_', ' ')}</Badge>)}
                            {r.target_departments?.map(x => <Badge key={x} variant="outline" className="text-[10px] bg-purple-50 text-purple-700">{x}</Badge>)}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 font-bold text-slate-700">{r.recipient_count}</td>
                      <td className="py-2 px-2">
                        <button onClick={(e) => { e.stopPropagation(); setDrillDown({ row: r, kind: 'ack' }); }} className="text-emerald-700 font-semibold hover:underline" data-testid={`notice-ack-${r.id}`}>
                          {r.ack_count}
                        </button>
                      </td>
                      <td className="py-2 px-2">
                        <button onClick={(e) => { e.stopPropagation(); setDrillDown({ row: r, kind: 'pending' }); }} className="text-amber-700 font-semibold hover:underline" data-testid={`notice-pending-${r.id}`}>
                          {r.pending_count}
                        </button>
                      </td>
                      <td className="py-2 px-2 min-w-[120px]">
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${r.ack_percent}%` }} />
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5">{r.ack_percent}%</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Item 7 — Full Notice Detail Dialog with Acknowledge button */}
      <Dialog open={!!noticeDetail} onOpenChange={(o) => !o && setNoticeDetail(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="notice-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell size={18} className="text-emerald-600" />
              {noticeDetail?.title}
            </DialogTitle>
          </DialogHeader>
          {noticeDetail && (
            <div className="space-y-4 mt-2">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline" className="capitalize bg-slate-50">{noticeDetail.category || 'general'}</Badge>
                {noticeDetail.priority && noticeDetail.priority !== 'normal' && (
                  <Badge className={
                    noticeDetail.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                    noticeDetail.priority === 'high' ? 'bg-amber-100 text-amber-700' : ''
                  }>{noticeDetail.priority}</Badge>
                )}
                <span className="text-slate-400">Published {noticeDetail.publish_date}</span>
                {noticeDetail.expiry_date && <span className="text-slate-400">· Expires {noticeDetail.expiry_date}</span>}
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-100 p-4 whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">
                {noticeDetail.content || '(No content)'}
              </div>

              {(noticeDetail.target_roles?.length > 0 || noticeDetail.target_departments?.length > 0) && (
                <div>
                  <p className="text-[11px] uppercase text-slate-400 font-semibold mb-1">Audience</p>
                  <div className="flex flex-wrap gap-1">
                    {(noticeDetail.target_roles || []).map(r => (
                      <Badge key={`r-${r}`} variant="outline" className="text-[10px] bg-blue-50 text-blue-700 capitalize">{r.replace(/_/g, ' ')}</Badge>
                    ))}
                    {(noticeDetail.target_departments || []).map(d => (
                      <Badge key={`d-${d}`} variant="outline" className="text-[10px] bg-purple-50 text-purple-700">{d}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Live ack stats from the row */}
              {noticeDetail._row && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-slate-50 py-2">
                    <p className="text-lg font-bold text-slate-700">{noticeDetail._row.recipient_count}</p>
                    <p className="text-[10px] text-slate-400">Recipients</p>
                  </div>
                  <div className="rounded-lg bg-emerald-50 py-2">
                    <p className="text-lg font-bold text-emerald-700">{noticeDetail._row.ack_count}</p>
                    <p className="text-[10px] text-emerald-600">Acknowledged</p>
                  </div>
                  <div className="rounded-lg bg-amber-50 py-2">
                    <p className="text-lg font-bold text-amber-700">{noticeDetail._row.pending_count}</p>
                    <p className="text-[10px] text-amber-600">Pending</p>
                  </div>
                </div>
              )}

              {/* Acknowledge button — when current user hasn't acked yet */}
              {!(noticeDetail.acknowledged_by || []).includes(user?.id) ? (
                <Button
                  onClick={acknowledgeNotice}
                  disabled={ackBusy}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="notice-acknowledge-btn"
                >
                  <CheckCircle2 size={16} className="mr-2" />
                  {ackBusy ? 'Saving…' : 'Acknowledge Notice'}
                </Button>
              ) : (
                <div className="flex items-center justify-center gap-2 py-3 bg-emerald-50 rounded-lg text-emerald-700 text-sm font-medium" data-testid="notice-already-acked">
                  <CheckCircle2 size={16} /> You have acknowledged this notice
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Drill-down dialog */}
      <Dialog open={!!drillDown} onOpenChange={() => setDrillDown(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {drillDown?.kind === 'ack' ? 'Acknowledged Users' : 'Pending Users'} — {drillDown?.row?.title}
            </DialogTitle>
          </DialogHeader>
          {drillDown && (
            <div className="space-y-1.5 mt-2">
              {(drillDown.kind === 'ack' ? drillDown.row.ack_users : drillDown.row.pending_users).length === 0 ? (
                <p className="text-sm text-slate-400 italic text-center py-6">None</p>
              ) : (
                (drillDown.kind === 'ack' ? drillDown.row.ack_users : drillDown.row.pending_users).map((u, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 text-sm">
                    <div>
                      <p className="font-medium text-slate-700">{u.name}</p>
                      <p className="text-xs text-slate-400">{u.email}</p>
                    </div>
                    <span className="text-xs font-mono text-slate-500">{u.user_id}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, color }) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl ${color} flex items-center justify-center shadow-sm`}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-xl font-bold text-slate-800">{value}</p>
        <p className="text-xs text-slate-500">{label}{sub ? ` ${sub}` : ''}</p>
      </div>
    </CardContent>
  </Card>
);

export default NoticeDashboardPage;
